import { siteCurrencyAtom } from "@/config.ts";
import { mustFindBy } from "@/mock-api/mustFindBy.ts";
import { currenciesAtom } from "@/modules/currency/currencyInfo.ts";
import type { SiteAmount, UntaggedAmount } from "@/modules/currency/currencyTypes.ts";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { useCurrencyFormat } from "@/modules/pricing/currencies.ts";
import { hourlyToMonthly } from "@/modules/pricing/hourlyToMonthly.ts";
import { notNull } from "@warrenio/utils/notNull";
import { useAtomValue } from "jotai/react";
import type { ReactNode } from "react";
import { useNumberFormatter } from "react-aria";

interface CurrencyProps {
    value: number;
    /**
     * Maximum fractional digits to display.
     * When set to 0, the number is rounded up to the nearest integer.
     * Can be negative to start rounding non-fractional digits.
     */
    maximumFractionDigits?: number;
    /** Hide fractions when they are all zeros (eg. `2.00`) */
    hideTrailingZeroFractions?: boolean;
    /** Always display `0` value without fractions */
    noZeroFractions?: boolean;

    /** Always display some value greater than zero
     * (minimum possible within `maximumFractionDigits`) because it's not free.
     * Also prepend prefix to indicate it. */
    isNotFree?: boolean;

    /** Currency to use for formatting */
    currency: { code: string; rate?: number };
}

/** NB: Do not use directly, create a wrapper component for a specific "price type". */
function Currency({
    value,
    maximumFractionDigits = 10,
    hideTrailingZeroFractions = false,
    noZeroFractions = true,
    isNotFree,
    currency: { rate, code },
}: CurrencyProps): ReactNode {
    if (maximumFractionDigits < 0) {
        value = Math.round(value * 10 ** maximumFractionDigits) * 10 ** -maximumFractionDigits;
        maximumFractionDigits = 0;
    }

    if (rate != null) {
        value *= rate;
    }

    const checkPriceFormatter = useNumberFormatter({ maximumFractionDigits });
    if (isNotFree) {
        if (checkPriceFormatter.format(value) === "0") {
            // handle case where final rounded price would appear as zero, although it's not free
            // but too small amount to display with `maximumFractionDigits`
            maximumFractionDigits += 2;
            const factor = 10 ** maximumFractionDigits;
            value = Math.ceil(value * factor) / factor;
        }
    }

    const options: Intl.NumberFormatOptions = {
        style: "currency",
        currency: code,
        // NB: This is mandatory. Set to a large number of digits to simulate "no rounding".
        maximumFractionDigits,
    };

    const formatter = useNumberFormatter(options);
    const noFractionFormatter = useNumberFormatter({ ...options, minimumFractionDigits: 0 });

    const parts = formatter.formatToParts(value);
    // Use default currency rounding when there is a fractional part, otherwise _allow_ for no fractional digits
    // (depends on browser locale and currency)
    const isFractionAllZeros =
        hideTrailingZeroFractions && /^0+$/.test(parts.find((part) => part.type === "fraction")?.value ?? "");
    const isHideZero = noZeroFractions && value === 0;
    return isFractionAllZeros || isHideZero ? noFractionFormatter.format(value) : formatter.format(value);
}

export interface StandardCurrencyProps {
    value: SiteAmount | UntaggedAmount;
}

/** Use for monthly cost estimates.
 *
 * Automatically rounds to a smaller number of digits.
 */
export function CurrencyMonthly({
    value,
    accurate = false,
    isNotFree,
}: StandardCurrencyProps & {
    /** Add 1 extra digit of accuracy (compared to usual) before removing fractions (for eg. price modal) */
    accurate?: boolean;
    /** Indicates that this price is not free, so don't show 0 even if rounded price with maximum fractions is zero */
    isNotFree?: boolean;
}) {
    const currency = useCurrentCurrency();
    const { decimal_digits } = currency;

    const hasLotsOfDigits = decimal_digits === 0;
    const estPriceThreshold = hasLotsOfDigits ? (accurate ? Infinity : 10000) : accurate ? 100 : 10;
    const estPriceDigits = hasLotsOfDigits ? -3 : 0;

    const monthlyValue = hourlyToMonthly(value);
    const maximumFractionDigits = monthlyValue >= estPriceThreshold ? estPriceDigits : decimal_digits;

    return (
        <Currency
            value={monthlyValue}
            currency={currency}
            maximumFractionDigits={maximumFractionDigits}
            isNotFree={isNotFree}
        />
    );
}

interface CurrencyHourlyProps extends StandardCurrencyProps {
    /** Hide fractions when currency does not normally show them */
    compact?: boolean;
}

/** Use for unchanged raw prices from the backend: invoices, pricing tables, license costs.
 *
 * Displays an hourly price with full precision (unlimited decimal digits).
 */
export function CurrencyHourly({ value, compact = false }: CurrencyHourlyProps) {
    const currency = useCurrentCurrency();

    const maximumFractionDigits = compact && currency.decimal_digits === 0 ? 0 : undefined;
    return <Currency value={value} currency={currency} maximumFractionDigits={maximumFractionDigits} />;
}

export function CurrencyPerPeriod(props: CurrencyHourlyProps) {
    return <CurrencyHourly {...props} />;
}

/** Use for calculated prices shown eg. before resource creation.
 *
 * Displays an hourly price with reduced precision (one extra decimal digit beyond currency standard). This is to
 * prevent from floating point calculations (potentially resulting in long fractional parts) from overflowing the
 * available space.
 */
export function EstimatedCurrencyHourly({ value, compact = false }: CurrencyHourlyProps) {
    const currency = useCurrentCurrency();

    const maximumFractionDigits =
        compact && currency.decimal_digits === 0 ? 0 : currency.decimal_digits ? currency.decimal_digits + 1 : 0;

    return <Currency value={value} currency={currency} maximumFractionDigits={maximumFractionDigits} />;
}

/** Use for showing promotional amounts.
 *
 * Removes trailing zeroes whenever possible (to be compact / easy to read).
 */
export function CurrencyPromo({ value }: StandardCurrencyProps) {
    const currency = useCurrentCurrency();
    return (
        <Currency
            value={value}
            currency={currency}
            hideTrailingZeroFractions={true}
            maximumFractionDigits={!currency.isSite ? currency.decimal_digits : undefined}
        />
    );
}

/** Use for balances & sums and other amounts that need to be displayed accurately.
 *
 * Always shows currency-appropriate fractional digits.
 */
export function CurrencyBalance({ value }: StandardCurrencyProps) {
    const currency = useCurrentCurrency();
    return <Currency value={value} currency={currency} maximumFractionDigits={currency.decimal_digits} />;
}

export interface OtherCurrencyProps extends StandardCurrencyProps {
    currency: string;
}

/** Display an amount already in some other currency (without conversion). */
export function UnconvertedCurrencyBalance({ value, currency }: { value: number; currency: string }) {
    const { decimal_digits } = useCurrencyFormat(currency);
    return <Currency value={value} currency={{ code: currency }} maximumFractionDigits={decimal_digits} />;
}

/** Display an amount in another currency, `value` is in the site currency. */
export function ConvertedCurrencyBalance({ value, currency }: OtherCurrencyProps) {
    const displayCurrencies = useAtomValue(currenciesAtom);
    const info = mustFindBy(notNull(displayCurrencies, "displayCurrencies"), (f) => f.code, currency, "currency");

    return <Currency value={value} currency={info} maximumFractionDigits={info.decimal_digits} />;
}

/** Use to display some exchange rate.
 *
 * Always shows full decimal digits.
 */
export function CurrencyRate({ value, currency }: OtherCurrencyProps) {
    return <Currency value={value} hideTrailingZeroFractions currency={{ code: currency }} />;
}

/** eg. "1 EUR" (for displaying exchange rates) */
export function CurrencyOne({ currency }: { currency: string }) {
    return <Currency value={1} maximumFractionDigits={0} currency={{ code: currency }} />;
}

/** The currency symbol (eg. "USD") for the current currency. */
export function CurrencySymbol() {
    const currency = useCurrentCurrency();
    const { symbol } = useCurrencyFormat(currency.code);
    return symbol;
}

export function SiteCurrencySymbol() {
    const siteCurrency = useAtomValue(siteCurrencyAtom);
    const { symbol } = useCurrencyFormat(siteCurrency);
    return symbol;
}
